import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { isEmpty, isNil } from "lodash";

import ProductCarousel from "~/components/ProductCarousel";

import Image from "~/components/Image";
import ErrorBoundary from "~/components/ErrorBoundary";

import { sanitizeAndParseHTML } from "~/utilities/helpers";

const ImageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const CurrentImage = styled(Image)`
    max-width: 200px;
    margin: 0 auto;
`;

const CaptionWrapper = styled.div`
    text-align: center;
    max-width: 420px;
    margin: auto;
    min-height: 64px;

    p {
        margin-bottom: 0;
    }
`;

const Caption = styled.span`
    width: 100%;
    word-wrap: break-word;
`;

const getCarouselProps = ({
    productImages,
    image,
    setCurrentImage,
    showCarousel,
}) => {
    if (!showCarousel) {
        return {};
    }

    const carouselImageCount = 4;
    const selectImage = setCurrentImage;
    let anyCaptions = false;
    let products = productImages.map(
        ({ altText, caption, image: productImage, showCaption }) => {
            let altText_ = "",
                caption_ = "";
            if (showCaption) {
                altText_ = altText ?? productImage.altText;
                caption_ = caption ?? productImage.caption;
            } else {
                showCaption = false;
            }
            if (caption_?.length) {
                anyCaptions = true;
            }
            return {
                image: {
                    ...productImage,
                    showCaption,
                    caption: caption_,
                    altText: altText_,
                },
            };
        },
    );

    if (!isNil(image)) {
        products.unshift({
            image: {
                ...image,
                slug: "featuredImage",
                showCaption: !!image.caption,
            },
        });
    }

    products = products.filter((product) => product.image.sourceUrl);
    return { products, carouselImageCount, selectImage, anyCaptions };
};

const createProductImage = (productImage) => {
    const image = productImage.image;
    const showCaption = !!productImage.showCaption;

    let altText = "";
    let caption = "";

    if (showCaption) {
        altText = productImage.altText ?? image?.altText;
        caption = productImage.caption ?? image.caption;
    }

    return {
        ...image,
        showCaption,
        caption,
        altText,
    };
};

const getInitialImage = (image, productImages) => {
    if (image) {
        return {
            ...image,
            showCaption: !!image?.caption,
        };
    }

    const productImage = isEmpty(productImages) ? null : productImages[0];

    if (productImage) {
        return createProductImage(productImage);
    }

    return null;
};

const ProductImageCarousel = ({
    image = null,
    showCarousel = false,
    productImages = null,
}) => {
    const initialImage = getInitialImage(image, productImages);
    const [currentImage, setCurrentImage] = useState(initialImage);

    const productsCarouselProps = getCarouselProps({
        productImages,
        image,
        setCurrentImage,
        showCarousel,
    });

    return (
        <ErrorBoundary>
            {currentImage && (
                <ImageWrapper>
                    <CurrentImage {...currentImage} {...{ showCarousel }} />
                </ImageWrapper>
            )}
            {showCarousel ? (
                <>
                    {productsCarouselProps.anyCaptions && (
                        <CaptionWrapper>
                            <Caption>
                                {sanitizeAndParseHTML(currentImage?.caption)}
                            </Caption>
                        </CaptionWrapper>
                    )}
                    <ProductCarousel
                        {...productsCarouselProps}
                    ></ProductCarousel>
                </>
            ) : (
                !!currentImage?.caption && (
                    <CaptionWrapper>
                        <Caption>
                            {sanitizeAndParseHTML(currentImage.caption)}
                        </Caption>
                    </CaptionWrapper>
                )
            )}
        </ErrorBoundary>
    );
};

ProductImageCarousel.propTypes = {
    currentImage: PropTypes.shape({}),
    showCarousel: PropTypes.bool,
    productsCarouselProps: PropTypes.shape({}),
    image: PropTypes.shape({}),
    productImages: PropTypes.array,
};

export default ProductImageCarousel;
